import moment from "moment";
import { significantWords } from "./significantWords";

export const WEEKDAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"] as const;
export type Weekday = (typeof WEEKDAYS)[number];

/**
 * The date ranges a question can ask about. "This week" means the last 7 days,
 * as on Home, unless the question looks ahead. "last:friday" is the Friday
 * before today; "on:friday" is the last one or the coming one, by which way
 * the question looks. "thisTerm" can't be worked out: there are no school terms.
 * "allTime" ("ever") is also the default for questions that don't need a date.
 */
export type DateRangeKey =
  | "today"
  | "yesterday"
  | "tomorrow"
  | "thisWeek"
  | "thisWeekend"
  | "nextWeekend"
  | "last7Days"
  | "previous7Days"
  | "nextWeek"
  | "thisMonth"
  | "lastMonth"
  | "thisYear"
  | "lastYear"
  | "thisTerm"
  | "allTime"
  | `last:${Weekday}`
  | `on:${Weekday}`
  /** "28 October", "Oct 28", "5/11": a month and day; the year comes from which way the question looks */
  | `date:${number}-${number}`
  /** "the 28th": a day of this month, or the next one when it has passed */
  | `dom:${number}`
  /** "in November", "December": the whole month */
  | `month:${number}`;

/** From `start` up to, not including, `end`; `label` finishes a sentence ("…in the last 7 days"). */
export type DateRange = { start: Date; end: Date; label: string };

/** Whether a question is about what's happened ("what did we send on Monday?") or what's coming ("what's on Monday?"). */
export type DateDirection = "back" | "ahead";

const DAY = `(${WEEKDAYS.join("|")})\\b(?!'s)`;

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
const MONTH = "(jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sept?(?:ember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?)";
/** Month names that are also everyday words, which only count as a month after "in", "during" or a day number. */
const PLAIN_MONTH = "(january|february|april|june|july|august|september|october|november|december)";
const monthOf = (word: string) => MONTHS.indexOf(word.slice(0, 3)) + 1;

const PHRASES: Array<[RegExp, (found: RegExpMatchArray) => DateRangeKey]> = [
  [new RegExp(`\\b(\\d{1,2})(?:st|nd|rd|th)?\\s+(?:of\\s+)?${MONTH}\\b`), (m) => `date:${monthOf(m[2])}-${Number(m[1])}`],
  [new RegExp(`\\b${MONTH}\\s+(\\d{1,2})(?:st|nd|rd|th)?\\b`), (m) => `date:${monthOf(m[1])}-${Number(m[2])}`],
  [/\b(\d{1,2})\/(\d{1,2})(?:\/\d{2,4})?\b/, (m) => `date:${Number(m[2])}-${Number(m[1])}`],
  [/\bthe\s+(\d{1,2})(?:st|nd|rd|th)\b/, (m) => `dom:${Number(m[1])}`],
  [new RegExp(`\\b(?:in|during|for|throughout|all\\s+of)\\s+${MONTH}\\b`), (m) => `month:${monthOf(m[1])}`],
  [new RegExp(`\\b${PLAIN_MONTH}\\b`), (m) => `month:${monthOf(m[1])}`],
  [new RegExp(`\\b(?:last|previous) ${DAY}`), (m) => `last:${m[1] as Weekday}`],
  [new RegExp(`\\b${DAY}`), (m) => `on:${m[1] as Weekday}`],
  [/\btoday\b/, () => "today"],
  [/\byesterday\b/, () => "yesterday"],
  [/\btomorrow\b/, () => "tomorrow"],
  [/\b(this|past|last) (7|seven) days\b|\bpast week\b/, () => "last7Days"],
  [/\b(?:next|coming) weekend\b/, () => "nextWeekend"],
  [/\b(?:this |the )?weekend\b/, () => "thisWeekend"],
  [/\bthis week\b/, () => "thisWeek"],
  [/\b(last|previous) week\b/, () => "previous7Days"],
  [/\b(next|coming) week\b/, () => "nextWeek"],
  [/\bthis month\b/, () => "thisMonth"],
  [/\b(last|previous) month\b/, () => "lastMonth"],
  [/\bthis year\b/, () => "thisYear"],
  [/\b(last|previous) year\b/, () => "lastYear"],
  [/\b(this|last|next) term\b/, () => "thisTerm"],
  [/\b(all time|ever)\b/, () => "allTime"],
];

/** Words that, beside a date, just mean "the last question again". */
const FOLLOW_UP_WORDS = new Set([
  "about",
  "how",
  "many",
  "much",
  "same",
  "then",
  "now",
  "so",
  "far",
  "and",
  "or",
  "but",
  "over",
  "during",
  "from",
  "again",
  "this",
  "next",
]);

function findPhrase(text: string): { key: DateRangeKey; phrase: RegExp } | null {
  const lower = text.toLowerCase();
  for (const [phrase, keyOf] of PHRASES) {
    const found = lower.match(phrase);
    if (found) return { key: keyOf(found), phrase };
  }
  return null;
}

/** The date range a message mentions, if any. */
export function findDateRange(text: string): DateRangeKey | null {
  return findPhrase(text)?.key ?? null;
}

/** A message that only changes the date, like "what about last month?". */
export function isDateFollowUp(text: string): boolean {
  const found = findPhrase(text);
  if (!found) return false;
  const rest = significantWords(text.toLowerCase().replace(found.phrase, ""));
  return Array.from(rest).every((w) => FOLLOW_UP_WORDS.has(w));
}

const dayOf = (date: Date, offset = 0) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + offset);

const monthName = (date: Date) => date.toLocaleDateString("en-US", { month: "long" });

const shortDate = (date: Date) =>
  date.toLocaleDateString("en-US", { month: "short", day: "numeric" });

/** "Friday, October 9" */
export const dayLabel = (date: Date) =>
  date.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });

/** "on Friday, October 2" */
const onDay = (date: Date) => `on ${dayLabel(date)}`;

const oneDay = (start: Date, label: string): DateRange => ({ start, end: dayOf(start, 1), label });

/** The Calendar's week holding `date`, which starts where its moment localizer says (Sunday). */
const calendarWeekOf = (date: Date) => moment(date).startOf("week").toDate();

/**
 * The days a range covers, or null when they can't be worked out ("this term").
 * `direction` says which way a named day ("on Monday") and a week look.
 */
export function resolveDateRange(
  key: DateRangeKey,
  now: Date = new Date(),
  direction: DateDirection = "back"
): DateRange | null {
  const today = dayOf(now);
  const tomorrow = dayOf(now, 1);
  const ahead = direction === "ahead";

  // "28 October", "the 28th", "in November": that day or month, this year unless the question looks the other way
  if (key.startsWith("date:") || key.startsWith("dom:") || key.startsWith("month:")) {
    const [kind, value] = key.split(":");
    if (kind === "month") {
      const month = Number(value) - 1;
      if (month < 0 || month > 11) return null;
      let year = now.getFullYear();
      if (ahead && new Date(year, month + 1, 1) <= today) year += 1;
      if (!ahead && new Date(year, month, 1) > today) year -= 1;
      const start = new Date(year, month, 1);
      return { start, end: new Date(year, month + 1, 1), label: `in ${monthName(start)}` };
    }
    let date: Date;
    if (kind === "dom") {
      const day = Number(value);
      if (day < 1 || day > 31) return null;
      date = new Date(now.getFullYear(), now.getMonth(), day);
      if (ahead && date < today) date = new Date(now.getFullYear(), now.getMonth() + 1, day);
      if (!ahead && date > today) date = new Date(now.getFullYear(), now.getMonth() - 1, day);
    } else {
      const [month, day] = value.split("-").map(Number);
      if (month < 1 || month > 12 || day < 1 || day > 31) return null;
      date = new Date(now.getFullYear(), month - 1, day);
      // A date well behind us, asked about ahead, is next year's; one ahead of us, asked about back, last year's
      if (ahead && date < dayOf(today, -31)) date = new Date(now.getFullYear() + 1, month - 1, day);
      if (!ahead && date > dayOf(today, 31)) date = new Date(now.getFullYear() - 1, month - 1, day);
    }
    return oneDay(date, onDay(date));
  }

  if (key.startsWith("last:") || key.startsWith("on:")) {
    const [which, day] = key.split(":") as ["last" | "on", Weekday];
    const daysAgo = (now.getDay() - WEEKDAYS.indexOf(day) + 7) % 7;
    const offset =
      which === "last" ? -(daysAgo || 7) : ahead ? (7 - daysAgo) % 7 : -daysAgo;
    const date = dayOf(now, offset);
    return oneDay(date, onDay(date));
  }

  switch (key) {
    case "today":
      return oneDay(today, "today");
    case "yesterday":
      return oneDay(dayOf(now, -1), "yesterday");
    case "tomorrow":
      return oneDay(tomorrow, "tomorrow");
    case "thisWeek":
      if (ahead) {
        const start = calendarWeekOf(now);
        return { start, end: dayOf(start, 7), label: "this week" };
      }
      return resolveDateRange("last7Days", now);
    case "thisWeekend":
    case "nextWeekend": {
      // Saturday and Sunday: this coming one (or the one under way), or the one after
      const weekday = now.getDay();
      const saturday = weekday === 0 ? dayOf(now, -1) : dayOf(now, 6 - weekday);
      const start = key === "nextWeekend" ? dayOf(saturday, 7) : weekday === 0 ? today : saturday;
      return { start, end: dayOf(key === "nextWeekend" ? start : saturday, 2), label: key === "nextWeekend" ? "next weekend" : "this weekend" };
    }
    case "last7Days":
      return { start: dayOf(now, -6), end: tomorrow, label: "in the last 7 days" };
    case "previous7Days": {
      if (ahead) {
        const end = calendarWeekOf(now);
        return { start: dayOf(end, -7), end, label: "last week" };
      }
      const start = dayOf(now, -13);
      return {
        start,
        end: dayOf(now, -6),
        label: `from ${shortDate(start)} to ${shortDate(dayOf(now, -7))}`,
      };
    }
    case "nextWeek": {
      const start = dayOf(calendarWeekOf(now), 7);
      return { start, end: dayOf(start, 7), label: "next week" };
    }
    case "thisMonth":
      return {
        start: new Date(now.getFullYear(), now.getMonth(), 1),
        end: tomorrow,
        label: `so far in ${monthName(now)}`,
      };
    case "lastMonth": {
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      return {
        start,
        end: new Date(now.getFullYear(), now.getMonth(), 1),
        label: `in ${monthName(start)}`,
      };
    }
    case "thisYear":
      return {
        start: new Date(now.getFullYear(), 0, 1),
        end: tomorrow,
        label: `so far in ${now.getFullYear()}`,
      };
    case "lastYear":
      return {
        start: new Date(now.getFullYear() - 1, 0, 1),
        end: new Date(now.getFullYear(), 0, 1),
        label: `in ${now.getFullYear() - 1}`,
      };
    case "allTime":
      return { start: new Date(0), end: new Date(8.64e15), label: "in total" };
    case "thisTerm":
      return null;
  }
  return null;
}

export const isInRange = (iso: string, { start, end }: DateRange) => {
  const date = new Date(iso);
  return date >= start && date < end;
};

/** Whether something running from `start` to `end` falls in the range at all, as the Calendar shows it. */
export const overlapsRange = (start: Date, end: Date, range: DateRange) =>
  start < range.end && (end > range.start || start >= range.start);
